import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserConnection } from '../types';
import { connectionsApi } from '../services/api';

interface ConnectionRequest {
  id: string;
  fromUserId: string;
  fromUserName: string;
  fromUserMangoId: string;
  fromUserAvatar?: string;
  message?: string;
  createdAt: string;
}

const Connections: React.FC = () => {
  const navigate = useNavigate();
  useAuth();
  const [activeTab, setActiveTab] = useState<'connections' | 'requests' | 'find'>('connections');
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [pendingRequests, setPendingRequests] = useState<ConnectionRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [apiComingSoon, setApiComingSoon] = useState(false);

  const loadConnections = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      setApiComingSoon(false);

      const [connectionsRes, requestsRes] = await Promise.all([
        connectionsApi.getConnections(),
        connectionsApi.getPendingRequests(),
      ]);

      const errMsg = (connectionsRes?.error ?? requestsRes?.error ?? '').toLowerCase();
      if (!connectionsRes?.success && (errMsg.includes('coming soon') || errMsg.includes('not yet'))) {
        setApiComingSoon(true);
        setConnections([]);
        setPendingRequests([]);
      } else {
        if (connectionsRes?.success) setConnections(connectionsRes.data || []);
        if (requestsRes?.success) setPendingRequests(requestsRes.data || []);
        if (!connectionsRes?.success && connectionsRes?.error && !connectionsRes.error.toLowerCase().includes('coming soon')) {
          setError(connectionsRes.error);
        }
      }
    } catch (err) {
      console.error('Failed to load connections:', err);
      setError('Failed to load connections. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, []);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await connectionsApi.searchUsers(query);
      if (response.success) {
        setSearchResults(response.data || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const handleSendRequest = async (userId: string, message?: string) => {
    try {
      const response = await connectionsApi.sendRequest(userId, message);
      if (response.success) {
        loadConnections(true);
      }
    } catch (err) {
      console.error('Failed to send request:', err);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      const response = await connectionsApi.acceptRequest(requestId);
      if (response.success) {
        loadConnections(true);
      }
    } catch (err) {
      console.error('Failed to accept request:', err);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      const response = await connectionsApi.rejectRequest(requestId);
      if (response.success) {
        loadConnections(true);
      }
    } catch (err) {
      console.error('Failed to reject request:', err);
    }
  };

  // NOTE: Demo data removed - using live API data

  const getConnectionTypeColor = (type: string) => {
    switch (type) {
      case 'friend': return '#28a745';
      case 'following': return '#667eea';
      case 'family': return '#f093fb';
      default: return '#666';
    }
  };

  const getTrustLabel = (weight: number) => {
    if (weight >= 1.5) return { label: 'High Trust', color: '#28a745' };
    if (weight >= 1.2) return { label: 'Good Trust', color: '#20c997' };
    if (weight >= 1.0) return { label: 'Normal', color: '#17a2b8' };
    return { label: 'Building Trust', color: '#ffc107' };
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🤝 Connections</h1>
          <p style={{ margin: '10px 0 0 0', color: '#666' }}>
            Your friends, family, and contacts on Mango
          </p>
        </div>
        <button type="button" onClick={() => loadConnections(true)} disabled={refreshing} style={{ padding: '10px 20px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '10px', cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '14px' }}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {apiComingSoon && (
        <div style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #f0e8ff 100%)', padding: 24, borderRadius: 12, marginBottom: 24, border: '1px solid #d0e0f0' }}>
          <p style={{ margin: '0 0 12px 0', color: '#333', fontSize: 15 }}>🤝 Connections are planned for a future release. Connect with people in Groups or discover businesses in the meantime.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/groups" style={{ padding: '10px 20px', background: '#667eea', color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>Go to Groups</Link>
            <Link to="/businesses" style={{ padding: '10px 20px', background: '#17a2b8', color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>Discover Businesses</Link>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '15px',
        marginBottom: '25px'
      }}>
        {[
          { label: 'Friends', value: connections.filter(c => c.connectionType === 'friend').length, icon: '👥', color: '#28a745' },
          { label: 'Following', value: connections.filter(c => c.connectionType === 'following').length, icon: '👁', color: '#667eea' },
          { label: 'Family', value: connections.filter(c => c.connectionType === 'family').length, icon: '👨‍👩‍👧‍👦', color: '#f093fb' },
          { label: 'Pending', value: pendingRequests.length, icon: '⏳', color: '#ffc107' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{stat.label}</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '28px', fontWeight: '700', color: stat.color }}>
                  {stat.value}
                </p>
              </div>
              <span style={{ fontSize: '28px' }}>{stat.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #eee'
        }}>
          {[
            { id: 'connections', label: '👥 My Connections', count: connections.length },
            { id: 'requests', label: '⏳ Requests', count: pendingRequests.length },
            { id: 'find', label: '🔍 Find People' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                flex: 1,
                padding: '18px',
                background: activeTab === tab.id ? 'white' : '#f8f9fa',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #ff6b35' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                color: activeTab === tab.id ? '#ff6b35' : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span style={{
                  background: activeTab === tab.id ? '#ff6b35' : '#ddd',
                  color: activeTab === tab.id ? 'white' : '#666',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ padding: '25px' }}>
          {/* My Connections Tab */}
          {activeTab === 'connections' && (
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666' }}>Loading connections...</p>
                </div>
              ) : connections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <span style={{ fontSize: '48px' }}>👥</span>
                  <h3 style={{ margin: '20px 0 10px 0', color: '#333' }}>No connections yet</h3>
                  <p style={{ color: '#666', margin: '0 0 20px 0' }}>
                    Start by finding and connecting with other Mango users
                  </p>
                  <button
                    onClick={() => setActiveTab('find')}
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Find People
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {connections.map(conn => {
                    const trust = getTrustLabel(conn.trustWeight);
                    return (
                      <div
                        key={conn.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                          padding: '20px',
                          background: '#f8f9fa',
                          borderRadius: '12px'
                        }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '24px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {conn.userName.charAt(0)}
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>{conn.userName}</h3>
                            <span style={{
                              padding: '3px 10px',
                              background: getConnectionTypeColor(conn.connectionType) + '20',
                              color: getConnectionTypeColor(conn.connectionType),
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}>
                              {conn.connectionType}
                            </span>
                          </div>
                          <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#888' }}>
                            {conn.userMangoId}
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '8px' }}>
                            <span style={{
                              padding: '4px 10px',
                              background: trust.color + '20',
                              color: trust.color,
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              🤝 {trust.label} ({conn.trustWeight}x)
                            </span>
                            <span style={{ fontSize: '12px', color: '#888' }}>
                              Connected {new Date(conn.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => navigate('/messages')}
                            style={{
                              padding: '10px 16px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            💬 Message
                          </button>
                          <button
                            onClick={() => navigate('/wallet')}
                            style={{
                              padding: '10px 16px',
                              background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            🥭 Send Credits
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <>
              {pendingRequests.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <span style={{ fontSize: '48px' }}>✓</span>
                  <h3 style={{ margin: '20px 0 10px 0', color: '#333' }}>No pending requests</h3>
                  <p style={{ color: '#666' }}>You're all caught up!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {pendingRequests.map(req => (
                    <div
                      key={req.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        padding: '20px',
                        background: '#fff3e0',
                        borderRadius: '12px',
                        border: '1px solid #ffe0b2'
                      }}
                    >
                      <div style={{
                        width: '55px',
                        height: '55px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '22px',
                        fontWeight: '600',
                        flexShrink: 0
                      }}>
                        {req.fromUserName.charAt(0)}
                      </div>

                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>{req.fromUserName}</h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#888' }}>{req.fromUserMangoId}</p>
                        {req.message && (
                          <p style={{ 
                            margin: '10px 0 0 0', 
                            fontSize: '13px', 
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            "{req.message}"
                          </p>
                        )}
                        <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#888' }}>
                          Sent {new Date(req.createdAt).toLocaleDateString()}
                        </p>
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => handleAcceptRequest(req.id)}
                          style={{
                            padding: '12px 20px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}
                        >
                          ✓ Accept
                        </button>
                        <button
                          onClick={() => handleRejectRequest(req.id)}
                          style={{
                            padding: '12px 20px',
                            background: '#f0f0f0',
                            color: '#666',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Find People Tab */}
          {activeTab === 'find' && (
            <>
              <div style={{ marginBottom: '25px' }}>
                <p style={{ margin: '0 0 15px 0', color: '#666' }}>
                  Find other Mango users by their Mango ID or name
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                    placeholder="Enter Mango ID (e.g., MP-XX-123456) or name"
                    style={{
                      flex: 1,
                      padding: '14px 18px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '15px'
                    }}
                  />
                  <button
                    onClick={() => handleSearch(searchQuery)}
                    style={{
                      padding: '14px 28px',
                      background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Search
                  </button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Search Results</h3>
                  {searchResults.map(result => (
                    <div
                      key={result.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        padding: '20px',
                        background: '#f8f9fa',
                        borderRadius: '12px'
                      }}
                    >
                      <div style={{
                        width: '55px',
                        height: '55px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '22px',
                        fontWeight: '600'
                      }}>
                        {result.name.charAt(0)}
                      </div>

                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>{result.name}</h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#888' }}>
                          {result.mangoId}
                        </p>
                        {result.mutualConnections > 0 && (
                          <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#666' }}>
                            👥 {result.mutualConnections} mutual connection{result.mutualConnections > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>

                      <button
                        onClick={() => handleSendRequest(result.id)}
                        style={{
                          padding: '12px 24px',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}
                      >
                        + Connect
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Suggested Connections - live data when API available */}
              {!apiComingSoon && (
                <div style={{ marginTop: '30px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>
                    💡 Suggested Connections
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>
                    Search by Mango ID or name above. Suggestions will appear here when the Connections API is available.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Trust Weight Info */}
      <div style={{
        marginTop: '30px',
        background: 'linear-gradient(135deg, #667eea15, #764ba215)',
        borderRadius: '16px',
        padding: '25px',
        border: '1px solid #667eea30'
      }}>
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>
          🤝 About Trust Weight
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: 1.6 }}>
          Trust weight determines how much your connections affect your Mango Credit transactions. 
          Higher trust means:
        </p>
        <ul style={{ margin: '15px 0 0 0', paddingLeft: '20px', color: '#666', fontSize: '14px', lineHeight: 1.8 }}>
          <li><strong>Friends (1.5x)</strong> - Your closest connections with maximum trust</li>
          <li><strong>Family (1.3x)</strong> - Family members with high trust</li>
          <li><strong>Following (1.0x)</strong> - People you follow but haven't connected with mutually</li>
        </ul>
        <p style={{ margin: '15px 0 0 0', fontSize: '13px', color: '#888' }}>
          Build trust through regular transactions and interactions within the Mango network.
        </p>
      </div>
    </div>
  );
};

export default Connections;
